import React, { useState } from 'react';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Icon,
    PrimaryButton,
    Text,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { Trans, useTranslation } from 'react-i18next';
import { appInsightsService, TelemetryConstants } from 'app/utils/constants';
import './LegacyExperienceSwitchBanner.scss';

interface LegacyExperienceSwitchBannerProps {
    hasDraft: boolean;
    onSwitch: (deleteDraft: boolean) => Promise<void>;
}

const LegacyExperienceSwitchBanner: React.FC<LegacyExperienceSwitchBannerProps> = ({
    hasDraft,
    onSwitch,
}) => {
    const { t } = useTranslation();
    const titleId = useId('legacy-experience-title');
    const descriptionId = useId('legacy-experience-description');
    const confirmationDescriptionId = useId('legacy-experience-confirmation-description');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const switchExperience = async (deleteDraft: boolean) => {
        if (isSwitching) return;
        setIsSwitching(true);
        setErrorMessage('');
        appInsightsService.logEvent(TelemetryConstants.EXPERIENCE_SWITCH_STARTED, {
            fromExperience: 'previous',
            toExperience: 'new',
            hadDraft: String(hasDraft),
            deleteDraft: String(deleteDraft),
        });
        try {
            await onSwitch(deleteDraft);
            appInsightsService.logEvent(TelemetryConstants.EXPERIENCE_SWITCH_SUCCEEDED, {
                fromExperience: 'previous',
                toExperience: 'new',
                hadDraft: String(hasDraft),
                deleteDraft: String(deleteDraft),
            });
        } catch (error) {
            appInsightsService.logEvent(TelemetryConstants.EXPERIENCE_SWITCH_FAILED, {
                fromExperience: 'previous',
                toExperience: 'new',
                hadDraft: String(hasDraft),
                deleteDraft: String(deleteDraft),
                error: error instanceof Error ? error.message : 'unknown',
            });
            setErrorMessage(t("We couldn't switch experiences. Your request progress is unchanged."));
            setIsSwitching(false);
        }
    };

    const handleSwitchClick = () => {
        if (hasDraft) {
            setShowConfirmation(true);
            return;
        }
        void switchExperience(false);
    };

    return (
        <>
            <section
                className="legacy-experience-banner"
                aria-labelledby={`${titleId} ${descriptionId}`}
            >
                <Icon className="legacy-experience-info-icon" iconName="Info" aria-hidden="true" />
                <div className="legacy-experience-copy">
                    <Text className="legacy-experience-title" id={titleId}>
                        <Trans>You&apos;re using the previous ticket creation experience</Trans>
                    </Text>
                    <Text className="legacy-experience-description" id={descriptionId}>
                        <Trans>This experience will be retired soon. Switch to the new experience.</Trans>
                    </Text>
                </div>
                <DefaultButton
                    className="legacy-experience-switch-button"
                    text={t('Switch to new experience')}
                    onClick={handleSwitchClick}
                    disabled={isSwitching}
                    aria-describedby={`${titleId} ${descriptionId}`}
                    data-testid="switch-to-new-experience"
                />
            </section>

            <Dialog
                data-testid="legacy-experience-confirmation-dialog"
                hidden={!showConfirmation}
                onDismiss={isSwitching ? undefined : () => setShowConfirmation(false)}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: t('Delete your progress and switch?'),
                    subText: t('Review what will be deleted before switching to the new experience.'),
                    closeButtonAriaLabel: t('Close'),
                    showCloseButton: true,
                }}
                modalProps={{
                    isBlocking: isSwitching,
                    containerClassName: 'legacy-experience-confirmation-dialog',
                    subtitleAriaId: confirmationDescriptionId,
                }}
                minWidth={0}
            >
                <div className="legacy-experience-dialog-copy">
                    <Text id={confirmationDescriptionId}>
                        <Trans>Your request details and uploaded files will be permanently deleted.</Trans>
                    </Text>
                    <Text>
                        <Trans>
                            The new experience will open by default the next time you create a request. You can switch
                            back at any time.
                        </Trans>
                    </Text>
                    {errorMessage && (
                        <Text className="legacy-experience-error" role="alert">
                            {errorMessage}
                        </Text>
                    )}
                </div>
                <DialogFooter>
                    <DefaultButton
                        text={t('Keep editing')}
                        onClick={() => setShowConfirmation(false)}
                        disabled={isSwitching}
                        data-testid="keep-editing-button"
                    />
                    <PrimaryButton
                        className="legacy-experience-delete-button"
                        text={t('Delete and switch')}
                        onClick={() => void switchExperience(true)}
                        disabled={isSwitching}
                        data-testid="delete-draft-and-switch"
                    />
                </DialogFooter>
            </Dialog>
        </>
    );
};

export default LegacyExperienceSwitchBanner;
